import React, { useState, useEffect, createElement } from 'react';
import { Scale, Plus, Pencil, X, Eye, ArrowUp, ArrowDown, ListPlus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { requestPin } from '../../components/PinModal';
import { toast } from 'sonner';

// Operaciones sobre el árbol de cláusulas (hermana, hija, orden, borrar)
const cloneTree = (t) => JSON.parse(JSON.stringify(t));
function getList(tree, path) {
  let list = tree;
  for (const i of path.slice(0, -1)) list = list[i].children;
  return list;
}

function ClauseNode({ node, path, number, onChange }) {
  const idx = path[path.length - 1];
  return (
    <div className="border border-stone-200 rounded-lg p-3 bg-white" data-testid={`clause-${number}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-black text-amber-600">{number}.</span>
        <span className="text-[10px] text-stone-400 uppercase">Nivel {path.length}</span>
        <div className="flex-1" />
        <button type="button" title="Subir" onClick={() => onChange((t) => { const c = cloneTree(t); const l = getList(c, path); if (idx > 0) { [l[idx - 1], l[idx]] = [l[idx], l[idx - 1]]; } return c; })} className="text-stone-400 hover:text-stone-700 transition-colors" data-testid={`clause-up-${number}`}><ArrowUp className="w-4 h-4" /></button>
        <button type="button" title="Bajar" onClick={() => onChange((t) => { const c = cloneTree(t); const l = getList(c, path); if (idx < l.length - 1) { [l[idx + 1], l[idx]] = [l[idx], l[idx + 1]]; } return c; })} className="text-stone-400 hover:text-stone-700 transition-colors" data-testid={`clause-down-${number}`}><ArrowDown className="w-4 h-4" /></button>
        <button type="button" title="Añadir cláusula hermana" onClick={() => onChange((t) => { const c = cloneTree(t); getList(c, path).splice(idx + 1, 0, { text: '', children: [] }); return c; })} className="text-teal-500 hover:text-teal-700 transition-colors" data-testid={`clause-sibling-${number}`}><Plus className="w-4 h-4" /></button>
        <button type="button" title="Añadir subcláusula hija" onClick={() => onChange((t) => { const c = cloneTree(t); let n = c; for (const i of path.slice(0, -1)) n = n[i].children; n[idx].children.push({ text: '', children: [] }); return c; })} className="text-sky-500 hover:text-sky-700 transition-colors" data-testid={`clause-child-${number}`}><ListPlus className="w-4 h-4" /></button>
        <button type="button" title="Eliminar" onClick={() => onChange((t) => { const c = cloneTree(t); getList(c, path).splice(idx, 1); return c; })} className="text-red-400 hover:text-red-600 transition-colors" data-testid={`clause-delete-${number}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      <textarea
        value={node.text}
        onChange={(e) => onChange((t) => { const c = cloneTree(t); let n = c; for (const i of path.slice(0, -1)) n = n[i].children; n[idx].text = e.target.value; return c; })}
        rows={Math.max(2, Math.ceil(node.text.length / 110))}
        className="w-full border border-stone-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
        placeholder="Texto exacto de la cláusula…"
        data-testid={`clause-text-${number}`}
      />
      {node.children?.length > 0 && (
        <div className="mt-2 ml-5 space-y-2 border-l-2 border-stone-100 pl-3">
          {node.children.map((child, i) => createElement(ClauseNode, { key: i, node: child, path: [...path, i], number: `${number}.${i + 1}`, onChange }))}
        </div>
      )}
    </div>
  );
}

const STATUS_STYLE = {
  activa: 'bg-teal-100 text-teal-700',
  borrador: 'bg-amber-100 text-amber-700',
  historica: 'bg-stone-100 text-stone-500',
};

export default function AdminLegal() {
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null); // slug
  const [editing, setEditing] = useState(null); // { version, content } | { new: true, content }
  const [publishTarget, setPublishTarget] = useState(null); // version a publicar
  const [creatingDoc, setCreatingDoc] = useState(null); // { name, group }
  const [dragSlug, setDragSlug] = useState(null);

  const onDropTab = (targetSlug) => {
    if (!dragSlug || dragSlug === targetSlug) return;
    const list = docs.map((d) => d.slug);
    const from = list.indexOf(dragSlug);
    list.splice(from, 1);
    list.splice(list.indexOf(targetSlug) + (from <= list.indexOf(targetSlug) ? 1 : 0), 0, dragSlug);
    // Reordena visualmente y persiste
    const reordered = list.map((s) => docs.find((d) => d.slug === s));
    setDocs(reordered);
    api.reorderLegalDocs(list).then(() => toast.success('Orden guardado')).catch(() => toast.error('Error al guardar el orden'));
    setDragSlug(null);
  };

  const load = () => api.getLegalDocs().then((d) => {
    setDocs(d);
    if (!selected && d.length) setSelected(d[0].slug);
  }).catch(() => {});

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doc = docs.find((d) => d.slug === selected);
  const versions = (doc?.versions || []).slice().sort((a, b) => b.version.localeCompare(a.version, undefined, { numeric: true }));

  const saveVersion = async () => {
    const clauses = editing.clauses;
    if (!clauses?.length && !editing.content.trim()) { toast.error('El contenido no puede estar vacío'); return; }
    try {
      if (editing.new) {
        await api.createLegalVersion(selected, editing.content, clauses);
        toast.success('Nueva versión creada en estado Borrador');
      } else {
        await api.updateLegalVersion(selected, editing.version, editing.content, clauses);
        toast.success('Borrador guardado. LO ESCRITO = LO GUARDADO');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const publish = async () => {
    const token = await requestPin(`Publicar versión legal ${publishTarget}`);
    if (!token) return;
    try {
      await api.publishLegalVersion(selected, publishTarget, token);
      toast.success(`Versión ${publishTarget} publicada como Activa`);
      setPublishTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al publicar');
    }
  };

  const createDoc = async () => {
    if (!creatingDoc.name.trim()) { toast.error('Indica el nombre del documento'); return; }
    try {
      const doc = await api.createLegalDoc(creatingDoc);
      setCreatingDoc(null);
      setSelected(doc.slug);
      load();
      toast.success(`Documento "${doc.name}" creado. Añade su primera versión.`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear'); }
  };

  const changeGroup = async (group) => {
    try {
      await api.updateLegalDocMeta(selected, { group });
      load();
      toast.success('Grupo actualizado');
    } catch { toast.error('Error al cambiar grupo'); }
  };

  return (
    <div data-testid="admin-legal">
      <div className="flex items-center gap-3 mb-1">
        <Scale className="w-6 h-6 text-amber-500" />
        <h1 className="text-2xl font-black text-stone-800">Área Legal</h1>
      </div>
      <p className="text-sm text-stone-500 mb-6">Versionado legal con snapshot por pedido. LO ESCRITO = LO GUARDADO = LO PUBLICADO.</p>

      {['generales', 'especificas'].map((g) => (
        <div key={g} className="mb-3">
          <p className="text-[10px] font-black uppercase tracking-widest text-stone-400 mb-1.5">{g === 'generales' ? 'Condiciones Generales' : 'Condiciones Específicas'}</p>
          <div className="flex flex-wrap gap-2">
            {docs.filter((d) => (d.group || 'generales') === g).map((d) => (
              <button
                key={d.slug}
                draggable
                onDragStart={() => setDragSlug(d.slug)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDropTab(d.slug)}
                onClick={() => { setSelected(d.slug); setEditing(null); }}
                title="Arrastra para reordenar"
                className={`px-4 py-2 text-sm font-bold rounded-full border transition-colors cursor-grab active:cursor-grabbing ${selected === d.slug ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-500'} ${dragSlug === d.slug ? 'opacity-40' : ''}`}
                data-testid={`legal-tab-${d.slug}`}
              >
                {d.name}
              </button>
            ))}
            {g === 'especificas' && (
              <button onClick={() => setCreatingDoc({ name: '', group: 'especificas' })} className="px-4 py-2 text-sm font-bold rounded-full border border-dashed border-amber-400 text-amber-600 hover:bg-amber-50 transition-colors" data-testid="new-legal-doc">
                + Nuevo documento
              </button>
            )}
          </div>
        </div>
      ))}

      {creatingDoc && (
        <div className="bg-white rounded-lg border-2 border-amber-300 p-6 my-4 max-w-2xl" data-testid="legal-doc-create">
          <h2 className="font-black text-stone-800 mb-4">Nuevo documento legal</h2>
          <div className="flex flex-wrap gap-3">
            <input value={creatingDoc.name} onChange={(e) => setCreatingDoc({ ...creatingDoc, name: e.target.value })} placeholder="Nombre (ej. Política de Devoluciones)" className="flex-1 min-w-56 border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="legal-doc-name" />
            <select value={creatingDoc.group} onChange={(e) => setCreatingDoc({ ...creatingDoc, group: e.target.value })} className="border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="legal-doc-group">
              <option value="generales">Condiciones Generales</option>
              <option value="especificas">Condiciones Específicas</option>
            </select>
            <button onClick={createDoc} className="btn-natur px-5 py-2 rounded text-sm font-bold" data-testid="legal-doc-save">Crear</button>
            <button onClick={() => setCreatingDoc(null)} className="px-4 py-2 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
          </div>
          <p className="text-[11px] text-stone-400 mt-2">El documento no será visible en la web hasta publicar su primera versión con PIN Maestro.</p>
        </div>
      )}

      {doc && (
        <div className="max-w-4xl">
          <div className="flex justify-between items-center mb-4 mt-3">
            <label className="flex items-center gap-2 text-xs text-stone-500 font-bold">
              Grupo:
              <select value={doc.group || 'generales'} onChange={(e) => changeGroup(e.target.value)} className="border border-stone-300 rounded px-2 py-1.5 text-xs bg-white focus:outline-none" data-testid="legal-group-select">
                <option value="generales">Condiciones Generales</option>
                <option value="especificas">Condiciones Específicas</option>
              </select>
            </label>
            <button onClick={() => setEditing({ new: true, content: versions[0]?.content || '', clauses: versions[0]?.clauses || null })} className="btn-natur flex items-center gap-2 px-4 py-2.5 rounded font-bold text-sm" data-testid="new-legal-version">
              <Plus className="w-4 h-4" /> Nueva versión (borrador)
            </button>
          </div>

          {editing && (
            <div className="bg-white rounded-lg border-2 border-amber-300 p-6 mb-6" data-testid="legal-editor">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-black text-stone-800">{editing.new ? `Nueva versión de ${doc.name}` : `Editar borrador ${editing.version}`}</h2>
                <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-stone-700 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              {editing.clauses ? (
                <div className="space-y-2" data-testid="clause-editor">
                  {editing.clauses.map((c, i) => (
                    <ClauseNode key={i} node={c} path={[i]} number={`${i + 1}`} onChange={(updater) => setEditing((e) => ({ ...e, clauses: updater(e.clauses) }))} />
                  ))}
                  <button type="button" onClick={() => setEditing({ ...editing, clauses: [...editing.clauses, { text: '', children: [] }] })} className="w-full border-2 border-dashed border-amber-300 text-amber-600 hover:bg-amber-50 transition-colors rounded-lg py-2.5 text-sm font-bold" data-testid="add-root-clause">
                    + Añadir cláusula raíz
                  </button>
                </div>
              ) : (
                <>
              <textarea
                value={editing.content}
                onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                rows={12}
                className="w-full border border-stone-300 rounded px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="Texto legal exacto. Se guarda tal cual, sin modificaciones automáticas."
                data-testid="legal-content-textarea"
              />
                  <button type="button" onClick={() => setEditing({ ...editing, clauses: editing.content.trim() ? [{ text: editing.content, children: [] }] : [{ text: '', children: [] }], content: '' })} className="mt-2 text-xs font-bold text-teal-600 hover:text-teal-800 transition-colors" data-testid="convert-to-clauses">
                    Convertir a cláusulas numeradas (1., 1.1., 1.1.1.…)
                  </button>
                </>
              )}
              <div className="flex justify-end gap-3 mt-4">
                <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
                <button onClick={saveVersion} className="btn-natur px-6 py-2.5 rounded text-sm font-bold" data-testid="save-legal-version">Guardar</button>
              </div>
            </div>
          )}

          {publishTarget && (
            <div className="bg-white rounded-lg border-2 border-red-300 p-6 mb-6" data-testid="legal-publish-confirm">
              <h2 className="font-black text-stone-800 mb-2">Publicar versión {publishTarget}</h2>
              <p className="text-sm text-stone-500 mb-4">Acción sensible: requiere PIN Maestro. La versión activa actual pasará a Histórica. Los pedidos existentes conservan su snapshot.</p>
              <div className="flex gap-3">
                <button onClick={publish} className="bg-red-600 hover:bg-red-700 transition-colors text-white px-5 py-2.5 rounded text-sm font-bold" data-testid="legal-publish-confirm-button">Publicar</button>
                <button onClick={() => setPublishTarget(null)} className="px-4 py-2.5 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Versión</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Autor</th>
                  <th className="px-4 py-3 font-medium">Creada</th>
                  <th className="px-4 py-3 font-medium">Publicada</th>
                  <th className="px-4 py-3 w-32"></th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v.version} className="border-t border-stone-100" data-testid={`legal-version-${v.version}`}>
                    <td className="px-4 py-3 font-bold text-stone-800">{v.version}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-1 rounded ${STATUS_STYLE[v.status]}`}>{v.status}</span></td>
                    <td className="px-4 py-3 text-stone-600">{v.author}</td>
                    <td className="px-4 py-3 text-stone-500 text-xs">{v.createdAt ? new Date(v.createdAt).toLocaleDateString('es-ES') : '—'}</td>
                    <td className="px-4 py-3 text-stone-500 text-xs">{v.publishedAt ? new Date(v.publishedAt).toLocaleDateString('es-ES') : '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        {v.status === 'borrador' && (
                          <>
                            <button onClick={() => setEditing({ version: v.version, content: v.content || '', clauses: v.clauses || null })} className="text-stone-400 hover:text-amber-600 transition-colors" title="Editar borrador" data-testid={`edit-legal-${v.version}`}><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => setPublishTarget(v.version)} className="text-xs font-bold text-red-600 hover:text-red-700 transition-colors" data-testid={`publish-legal-${v.version}`}>Publicar</button>
                          </>
                        )}
                        {v.status === 'activa' && (
                          <a href={`/legal/${doc.slug}`} target="_blank" rel="noreferrer" className="text-stone-400 hover:text-teal-600 transition-colors" title="Ver pública"><Eye className="w-4 h-4" /></a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
